import { useEffect, useState } from 'react';
import { api } from './api.js';

// Per-tenant feature flags. The platform admin turns features on/off per
// tenant from stsgp.com; the server enforces them (requireFeature in
// server/src/db/features.js) — hiding UI here is just so a tenant never
// sees something they can't use.
//
// Loaded once per app load from GET /auth/me (and taken straight from the
// login / "View as client" response), so a change made in the admin panel
// applies on the tenant's next page load.
//
//   hasFeature('outsourcing')  — plain check (after features have loaded)
//   useFeature('outsourcing')  — hook for components; false until loaded,
//                                so gated UI never flashes in and back out
//
// Adding a feature: see README "Feature flags".

let enabled = null;              // Set<string> once loaded
let loading = null;              // in-flight /auth/me promise
const listeners = new Set();

function publish(keys) {
  enabled = new Set(keys || []);
  listeners.forEach(fn => fn());
}

export function setFeatures(keys) { publish(keys); }

export function loadFeatures() {
  if (!loading) {
    loading = api('/auth/me')
      .then(me => publish(me.features))
      .catch(() => { loading = null; });
  }
  return loading;
}

// Login / logout / impersonation: forget the previous session's features.
export function resetFeatures() {
  enabled = null;
  loading = null;
  listeners.forEach(fn => fn());
}

export function hasFeature(key) {
  return !!enabled && enabled.has(key);
}

export function useFeature(key) {
  const [, rerender] = useState(0);
  useEffect(() => {
    const fn = () => rerender(n => n + 1);
    listeners.add(fn);
    if (!enabled) loadFeatures();
    return () => listeners.delete(fn);
  }, []);
  return hasFeature(key);
}

// For guards that need to tell "not loaded yet" apart from "off".
export function useFeaturesLoaded() {
  const [, rerender] = useState(0);
  useEffect(() => {
    const fn = () => rerender(n => n + 1);
    listeners.add(fn);
    if (!enabled) loadFeatures();
    return () => listeners.delete(fn);
  }, []);
  return enabled !== null;
}
